import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import Card from "../atoms/Card"

import H3 from "../atoms/typography/H3"
import H4 from "../atoms/typography/H4"
import Subtitle from "../atoms/typography/Subtitle"
import H1 from "../atoms/typography/H1"
import TextButton from "../atoms/buttons/TextButton"
import { LocationOnIcon } from "../atoms/icons/LocationOnIcon"
import { FadeInWhenInView } from "../atoms/animations/FadeInWhenInView"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import BodyLarge from "../atoms/typography/BodyLarge"
import Body from "../atoms/typography/Body"
import { MediaQuery } from "../../utils/design-helpers"
import { ArrowRight } from "../atoms/icons/ArrowRight"
import LoadingSpinner from "../atoms/LoadingSpinner"
import Markdown from "react-markdown"
import { captureExceptionWithContext } from "../../utils/sentry"
import { useAboutPage } from "../../hooks/about/useAboutPage"

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};

  ${MediaQuery.XS} {
    padding: 8px;
  }
`

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  ${MediaQuery.XS} {
    margin-left: -2rem;
    margin-right: -2rem;
    width: 130%;
    margin-top: -2rem;
    height: 110%;
    margin-bottom: 2rem;
  }
`

const StatsSection = styled.div`
  display: flex;
  align-items: baseline;
`

const LocationSection = styled.div`
  display: flex;
  align-items: center;
`

const BoardMemberImage = styled.img`
  width: 100%;
  height: 280px;
  object-fit: cover;
`

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${MediaQuery.XS} {
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
    align-items: start;
  }
`

const AboutPage: React.FunctionComponent = () => {
  const onClickCareersPage = () => {
    window.open("https://careers.satcube.com", "_blank")
  }

  React.useEffect(() => {
    document.title = "About"
  }, [])

  const { aboutPageContent, error, loading } = useAboutPage()

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    //TODO: Should we return a temporary issue page here? redirect to home?
    captureExceptionWithContext(error, "Error fetching about page content")
  }

  const content = aboutPageContent?.items[0]

  return (
    <div>
      <Container>
        <HorizontalSpacer spacing={2} />
        <H1>About us</H1>
        <HorizontalSpacer spacing={4} />
        <Card>
          <ContentContainer>
            <Row justify="between">
              <Visible xs md>
                <Col md={12}>
                  <Image src={content?.firstAboutImage?.url} />
                </Col>
              </Visible>
              <Col lg={7}>
                <Subtitle>{content?.firstAboutTitle}</Subtitle>
                <HorizontalSpacer spacing={2} />
                <StatRow>
                  <StatsSection>
                    <Body>{content?.infoOne}</Body>
                  </StatsSection>
                  <LocationSection>
                    <LocationOnIcon />
                    <Spacer spacing={0.5} />
                    <Body>{content?.locationOne}</Body>
                  </LocationSection>

                  <LocationSection>
                    <LocationOnIcon />
                    <Spacer spacing={0.5} />
                    <Body>{content?.locationTwo}</Body>
                  </LocationSection>

                  <LocationSection>
                    <LocationOnIcon />
                    <Spacer spacing={0.5} />
                    <Body>{content?.locationThree}</Body>
                  </LocationSection>
                </StatRow>

                <HorizontalSpacer spacing={2} />
                <Body>
                  <Markdown>{content?.firstAboutContent}</Markdown>
                </Body>
                <HorizontalSpacer spacing={2} />
                <TextButton title="Visit our career page" icon={<ArrowRight />} onClick={onClickCareersPage} />
              </Col>
              <Hidden xs md>
                <Col lg={5}>
                  <Image src={content?.firstAboutImage?.url} />
                </Col>
              </Hidden>
            </Row>
            <HorizontalSpacer spacing={8} />
            <FadeInWhenInView delay={0.5}>
              <Row justify="center">
                <Col lg={12}>
                  <Subtitle textAlign="center">{content?.quoteTitle}</Subtitle>
                  <HorizontalSpacer spacing={1} />
                </Col>
                <Col lg={10}>
                  <BodyLarge textAlign="center">
                    <Markdown>{content?.quote}</Markdown>
                  </BodyLarge>
                </Col>
              </Row>
            </FadeInWhenInView>
            <HorizontalSpacer spacing={8} />
            <Row justify="between">
              <Col lg={6}>
                <Image src={content?.secondAboutImage?.url} />
              </Col>
              <Col lg={6}>
                <Subtitle>{content?.secondAboutTitle}</Subtitle>
                <HorizontalSpacer spacing={2} />
                <Row justify="between" align="center">
                  <Col lg={3} md={6} xs={6}>
                    <Body>{content?.valueOne}</Body>
                  </Col>
                  <Col lg={3} md={6} xs={6}>
                    <Body>{content?.valueTwo}</Body>
                  </Col>
                  <Col lg={3} md={6} xs={6}>
                    <Body>{content?.valueThree}</Body>
                  </Col>
                  <Col lg={3} md={6} xs={6}>
                    <Body>{content?.valueFour}</Body>
                  </Col>
                </Row>
                <HorizontalSpacer spacing={2} />
                <Body>
                  <Markdown>{content?.secondAboutContent}</Markdown>
                </Body>
                <HorizontalSpacer spacing={1} />
                <img src="https://images.ctfassets.net/kzqg847wz5jr/1Uxs5NGDkVdjocnZ2ecVse/409d4853033e127688220b942d28a10b/singature.png" />
                <H3>Jakob Kallmer</H3>
                <Body>CEO Satcube</Body>
              </Col>
            </Row>

            <HorizontalSpacer spacing={8} />
            <Row>
              <Col lg={6}>
                <FadeInWhenInView>
                  <Row>
                    <Col md={4}>
                      <BoardMemberImage src="https://images.ctfassets.net/kzqg847wz5jr/4MKqSfcS4CDiRWETJPTYJi/f4feec134f299be01c930634f42c43ac/Portr-tt.webp" />
                    </Col>
                    <Col md={8}>
                      <div>
                        <Subtitle>Jakob Kallmér</Subtitle>
                        <HorizontalSpacer spacing={1} />
                        <H4>CEO / Founder</H4>
                        <HorizontalSpacer spacing={1} />
                        <Body>
                          Is an entrepreneur with a vision of expanding the availability of satellite terminals to a
                          mass market. Jakob combines deep technological expertise with market knowledge and the ability
                          to recruit the people needed to make the vision a reality. Jakob holds a MSc in Physics from
                          Uppsala University and has extensive knowledge of law and design management.
                        </Body>
                      </div>
                    </Col>
                  </Row>
                </FadeInWhenInView>
              </Col>
              <Col lg={6}>
                <Visible xs md>
                  <HorizontalSpacer spacing={2} />
                </Visible>
                <FadeInWhenInView>
                  <Row>
                    <Col md={4}>
                      <BoardMemberImage src="https://images.ctfassets.net/kzqg847wz5jr/36ku2Xmndx3XTjqyzu3cNb/6cd442b387099347027b686a54f0b062/Ska-rmavbild-2023-04-03-kl-10-16-24.png" />
                    </Col>
                    <Col md={8}>
                      <div>
                        <Subtitle>Mats Andersson</Subtitle>
                        <HorizontalSpacer spacing={1} />
                        <H4>Chairman of the board</H4>
                        <HorizontalSpacer spacing={1} />
                        <Body>
                          Is an entrepreneur and intrapreneur with more than 30 years international wireless and telecom
                          experience working at various engineering and management positions at Ericsson and Saab
                          Ericsson Space. During his 5 years as CEO, the start-up company Bluetest grew rapidly to
                          become the world leading MIMO Over-The-Air (OTA) test company. Mats has three university
                          degrees (Lic. Eng., M. Sc. and B.A.) from Chalmers University of Technology and University of
                          Gothenburg.
                        </Body>
                      </div>
                    </Col>
                  </Row>
                </FadeInWhenInView>
              </Col>
            </Row>
            <HorizontalSpacer spacing={4} />
            <Row>
              <Col lg={6}>
                <FadeInWhenInView>
                  <Row>
                    <Col md={4}>
                      <BoardMemberImage src="https://images.ctfassets.net/kzqg847wz5jr/1NFCLMCWg2IBIup1Uho7ka/1104351807c7476d020b4f93ec9615bf/Ska-rmavbild-2023-04-03-kl-10-28-25.png" />
                    </Col>
                    <Col md={8}>
                      <div>
                        <Subtitle>Johan Koch</Subtitle>
                        <HorizontalSpacer spacing={1} />
                        <H4>Board member</H4>
                        <HorizontalSpacer spacing={1} />
                        <Body>a serial entrepreneur and angel investor.</Body>
                      </div>
                    </Col>
                  </Row>
                </FadeInWhenInView>
              </Col>
            </Row>
            <HorizontalSpacer spacing={4} />
          </ContentContainer>
        </Card>

        <HorizontalSpacer spacing={3} />

        <HorizontalSpacer spacing={3} />
      </Container>
      <HorizontalSpacer spacing={2} />
    </div>
  )
}

export default AboutPage
